import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Target, Sparkles, AlertTriangle, Activity,
  TrendingUp, ListChecks, CheckCircle2, Check, Zap, X,
  Handshake, Users,
} from 'lucide-react';
import { T, ROLES, HEALTH, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { fmtRelative, fmtDeadline, pct, fmt, goalHealth, fmtMinutes, timeFromMin } from '../lib/helpers';
import { autoScheduleTasks } from '../lib/scheduler';
import { computePriorityStack, computeVentureBalance } from '../lib/priorityEngine';
import { useTimeOfDay } from '../lib/hooks/useTimeOfDay';
import { Glass } from '../components/glass/Glass';
import { Btn } from '../components/primitives/Btn';
import { RoleBadge, HealthBadge } from '../components/primitives/Badge';
import { ProgressBar } from '../components/primitives/ProgressBar';
import { Md } from '../components/primitives/Md';
import { SectionHeader } from '../components/cards/SectionHeader';
import { Collapsible } from '../components/cards/Collapsible';
import { TaskPopover } from '../components/cards/TaskPopover';
import { NowCard } from '../components/cards/NowCard';
import { UpNextQueue } from '../components/cards/UpNextQueue';
import { DeferMenu } from '../components/cards/DeferMenu';
import { completeTask } from '../lib/taskActions';

// Strip markdown noise so collapsible previews read like prose, not raw markup
const plainTextPreview = (md) => {
  if (!md) return '';
  return md
    .replace(/^#{1,6}\s+/gm, '')          // headings
    .replace(/^\s*[-*•]\s+/gm, '')        // list bullets
    .replace(/\*\*([^*]+)\*\*/g, '$1')    // bold
    .replace(/\*([^*]+)\*/g, '$1')        // italic
    .replace(/`([^`]+)`/g, '$1')          // inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/\n{2,}/g, ' · ')            // paragraph breaks
    .replace(/\n/g, ' ')                  // newlines
    .trim();
};

// SmartSchedule sub-component (kept inline for now)
const SmartSchedule = ({ calendarEvents, tasks, scheduledBlocks, onToggleTask, onUpdate, onRefresh }) => {
  const [now, setNow] = useState(new Date());
  const [selectedTask, setSelectedTask] = useState(null);
  // Ticked-but-not-yet-refetched: paint the check immediately so the tick
  // never feels dead while the refetch is in flight.
  const [justDone, setJustDone] = useState(new Set());
  const timelineRef = React.createRef();

  const tickTask = (task) => {
    setJustDone(prev => new Set(prev).add(task.id));
    onToggleTask(task);
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const nowMin = now.getHours() * 60 + now.getMinutes();
  // A task deferred to a later day must not resurface on today's timeline or
  // its "couldn't fit today" overflow — the client fallback scheduler doesn't
  // read earliest_start, so exclude deferred tasks here at the source.
  const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);
  const openTasks = tasks.filter(t =>
    !t.done && !(t.earliest_start && new Date(t.earliest_start) > endOfToday)
  );

  // Prefer server-persisted scheduled blocks when available
  const todayStr = now.toISOString().split('T')[0];
  const todayServerBlocks = (scheduledBlocks || []).filter(b => b.date === todayStr);
  const useServerBlocks = todayServerBlocks.length > 0;

  let scheduled, overflow;
  if (useServerBlocks) {
    // Build scheduled array from server blocks + calendar events
    const calBlocks = calendarEvents.map((e, i) => {
      const [h, m] = (e.time || '00:00').split(':').map(Number);
      const startMin = h * 60 + m;
      let endMin;
      if (e.endTime) {
        const [eh, em] = e.endTime.split(':').map(Number);
        endMin = eh * 60 + em;
      } else {
        endMin = startMin + 60;
      }
      return { startMin, endMin, type: 'event', item: e };
    });
    const taskBlocks = todayServerBlocks.map(b => ({
      startMin: b.start_min,
      endMin: b.end_min,
      type: 'task',
      item: b.tasks || { id: b.task_id, title: 'Task' },
      duration: b.end_min - b.start_min,
      truncated: false,
    }));
    scheduled = [...calBlocks, ...taskBlocks].sort((a, b) => a.startMin - b.startMin);
    // Overflow = tasks not in any server block
    const scheduledTaskIds = new Set(todayServerBlocks.map(b => b.task_id));
    overflow = openTasks.filter(t => !scheduledTaskIds.has(t.id) && t.priority <= 2);
  } else {
    ({ scheduled, overflow } = autoScheduleTasks(calendarEvents, openTasks, nowMin));
  }

  const allMins = scheduled.filter(s => s.startMin != null).map(s => s.startMin);
  const eventsStart = allMins.length > 0 ? Math.min(Math.floor(Math.min(...allMins) / 60) * 60, 7 * 60) : 7 * 60;
  // As the day progresses, start the timeline ~1 hour before now instead of showing the full morning
  const scrollStart = Math.floor((nowMin - 60) / 30) * 30;
  const timelineStart = Math.max(eventsStart, scrollStart);
  const allEnds = scheduled.filter(s => s.endMin != null).map(s => s.endMin);
  const timelineEnd = allEnds.length > 0 ? Math.max(Math.ceil(Math.max(...allEnds) / 60) * 60, 21 * 60) : 21 * 60;

  const hours = [];
  // timelineStart can land on a half-hour late in the day; start labels on the
  // first whole hour at or after it so none render above the timeline.
  for (let h = Math.ceil(timelineStart / 60); h <= Math.floor(timelineEnd / 60); h++) hours.push(h);

  const totalMinutes = timelineEnd - timelineStart;
  const pxPerMin = 1.4;
  const totalHeight = totalMinutes * pxPerMin;
  const taskCount = scheduled.filter(s => s.type === 'task').length;
  const eventCount = scheduled.filter(s => s.type === 'event').length;

  const openPopover = (task, block, e) => {
    e.stopPropagation();
    const tl = timelineRef.current;
    if (!tl) return;
    const blockTop = Math.max((block.startMin - timelineStart) * pxPerMin, 0);
    setSelectedTask({ task, block, rect: { top: blockTop, left: 0 } });
  };

  return (
    <Glass style={{ padding: 24 }} hover={false}>
      <SectionHeader icon={Calendar} iconBg={T.blueLight} iconColor={T.blue}
        title="Smart Schedule" subtitle={`${taskCount} tasks · ${eventCount} events`}
        right={<span style={{ fontSize: 10, fontWeight: 600, color: T.green, letterSpacing: '0.04em' }}>AUTO</span>}
      />

      {scheduled.length === 0 && openTasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <CheckCircle2 size={20} color={T.green} style={{ marginBottom: 6 }} />
          <div style={{ fontSize: 13, color: T.textMuted }}>No tasks or events today.</div>
        </div>
      ) : (
        <div style={{ position: 'relative', marginTop: 8 }}>
          <div ref={timelineRef} style={{ position: 'relative', height: totalHeight, marginLeft: 48 }}>
            {hours.map(h => {
              const top = (h * 60 - timelineStart) * pxPerMin;
              return (
                <div key={h} style={{ position: 'absolute', top, left: -48, right: 0, display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 10, color: T.textLight, fontWeight: 500, width: 40, textAlign: 'right', fontFamily: "'SF Mono', Menlo, Monaco, monospace", paddingRight: 8, fontFeatureSettings: '"tnum"' }}>
                    {`${String(h).padStart(2, '0')}:00`}
                  </span>
                  <div style={{ flex: 1, borderTop: '1px solid rgba(0,0,0,0.04)', marginTop: 5 }} />
                </div>
              );
            })}

            {nowMin >= timelineStart && nowMin <= timelineEnd && (
              <div style={{ position: 'absolute', top: (nowMin - timelineStart) * pxPerMin, left: -10, right: 0, zIndex: 10, display: 'flex', alignItems: 'center' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.red, flexShrink: 0 }} />
                <div style={{ flex: 1, height: 1.5, background: T.red }} />
              </div>
            )}

            {scheduled.map((block, i) => {
              // The timeline window advances to ~1h before now as the day goes
              // on; drop blocks that finished before it so they don't render at
              // a negative offset and spill over the card above.
              if (block.endMin != null && block.endMin <= timelineStart) return null;
              const rawTop = (block.startMin - timelineStart) * pxPerMin;
              const top = Math.max(rawTop, 0);
              // A block straddling the window start is clipped at the top, so
              // trim its height by the clipped amount (rawTop is negative here).
              const height = Math.max((block.endMin - block.startMin) * pxPerMin + Math.min(rawTop, 0), 28);
              const isPast = block.endMin < nowMin;

              if (block.type === 'event') {
                const roleKey = block.item.role?.toLowerCase();
                const r = roleKey ? ROLES[roleKey] : null;
                return (
                  <motion.div key={`e-${i}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: isPast ? 0.5 : 1, x: 0 }} transition={{ delay: i * 0.03, ...springs.gentle }} style={{
                    position: 'absolute', top, left: 0, right: 0, height,
                    background: r ? r.light : 'rgba(74,158,204,0.08)',
                    border: `1px solid ${r ? r.border : 'rgba(74,158,204,0.20)'}`,
                    borderRadius: 8, padding: '6px 10px',
                    borderLeft: `3px solid ${r ? r.color : T.blue}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 10, color: r?.color || T.blue, fontWeight: 600, fontFamily: "'SF Mono', Menlo, Monaco, monospace", fontFeatureSettings: '"tnum"' }}>{block.item.time}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text, flex: 1, lineHeight: 1.2 }}>{block.item.desc?.replace(/\s*\[.*?\]/, '').replace(/\s*—.*/, '') || block.item.title}</span>
                      {r && <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: r.light, color: r.color, flexShrink: 0 }}>{r.label}</span>}
                    </div>
                  </motion.div>
                );
              }

              if (block.type === 'task') {
                const task = block.item;
                const role = task.roles || task.goals?.roles;
                const r = role?.slug ? ROLES[role.slug] : null;
                const isSelected = selectedTask?.task.id === task.id;
                const ticked = justDone.has(task.id) || task.done;
                return (
                  <motion.div key={`t-${i}`} onClick={(e) => openPopover(task, block, e)}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: ticked ? 0.4 : isPast && !isSelected ? 0.45 : 1, x: 0 }}
                    transition={{ delay: i * 0.03, ...springs.gentle }}
                    whileHover={{ background: 'rgba(255,255,255,0.85)' }}
                    style={{
                      position: 'absolute', top, left: 0, right: 0, height,
                      background: isSelected ? 'rgba(255,255,255,0.95)' : (isPast ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.7)'),
                      border: `1px solid ${isSelected ? T.amberBorder : 'rgba(0,0,0,0.06)'}`,
                      borderRadius: 8, padding: '6px 10px',
                      borderLeft: `3px solid ${task.priority === 1 ? T.red : (r?.color || T.textLight)}`,
                      overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 8,
                      cursor: 'pointer', boxShadow: isSelected ? T.shadow : 'none',
                    }}>
                    <button onClick={(e) => { e.stopPropagation(); if (!ticked) tickTask(task); }} title="Mark done" style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      border: `1.5px solid ${ticked ? T.green : 'rgba(0,0,0,0.15)'}`,
                      background: ticked ? T.greenLight : 'transparent',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s ease',
                    }}>
                      {ticked && <Check size={10} color={T.green} />}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, lineHeight: 1.2, textDecoration: ticked ? 'line-through' : 'none' }}>{task.title}</div>
                      {height > 36 && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center' }}>
                          {r && <RoleBadge roleSlug={role.slug} small />}
                          <span style={{ fontSize: 10, color: T.textMuted }}>{fmtMinutes(block.duration)}</span>
                        </div>
                      )}
                    </div>
                    {task.priority === 1 && <div style={{ fontSize: 9, fontWeight: 700, color: T.red, flexShrink: 0, letterSpacing: '0.04em' }}>P1</div>}
                    <span style={{ fontSize: 10, color: T.textLight, flexShrink: 0, fontFamily: "'SF Mono', Menlo, Monaco, monospace", fontFeatureSettings: '"tnum"' }}>{timeFromMin(block.startMin)}</span>
                  </motion.div>
                );
              }
              return null;
            })}

            {selectedTask && (
              <TaskPopover task={selectedTask.task} block={selectedTask.block} anchorRect={selectedTask.rect}
                onClose={() => setSelectedTask(null)} onUpdate={() => { setSelectedTask(null); onUpdate(); }} />
            )}
          </div>

          {overflow.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <AlertTriangle size={12} color={T.amber} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.amber, letterSpacing: '0.04em' }}>
                  {overflow.length} {overflow.length === 1 ? 'task' : 'tasks'} couldn't fit today
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {overflow.map(t => {
                  const ticked = justDone.has(t.id);
                  return (
                    <div key={t.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      borderRadius: 6, background: 'rgba(212,146,30,0.04)', border: '1px solid rgba(212,146,30,0.10)',
                      opacity: ticked ? 0.5 : 1,
                    }}>
                      <button onClick={() => { if (!ticked) tickTask(t); }} title="Mark done" style={{
                        width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                        border: `1.5px solid ${ticked ? T.green : 'rgba(0,0,0,0.15)'}`,
                        background: ticked ? T.greenLight : 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {ticked && <Check size={9} color={T.green} />}
                      </button>
                      <span style={{ fontSize: 12, color: T.textMid, flex: 1, textDecoration: ticked ? 'line-through' : 'none' }}>{t.title}</span>
                      <span style={{ fontSize: 10, color: T.textMuted }}>{fmtMinutes(t.effortMin)}</span>
                      {t.priority === 1 && <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.red }} />}
                      <DeferMenu task={t} onDeferred={onRefresh} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Glass>
  );
};

export const TodayView = ({ inbox, tasks, goals, milestones, conflicts, agentLog, liveCalendar, scheduledBlocks, promises = [], meetingBriefs = [], onUpdate, onNavigate, triggerScheduler }) => {
  const { greeting, config } = useTimeOfDay();
  const brief = inbox.find(i => i.subject?.includes('Morning Brief'));
  const prioritisation = inbox.find(i => i.subject?.includes('Prioritisation'));
  const likenInsight = inbox.find(i => i.subject?.includes('Liken Insight'));
  const p1Tasks = tasks.filter(t => t.priority === 1 && !t.done);

  let calendarEntries = [];
  if (liveCalendar && liveCalendar.length > 0) {
    calendarEntries = liveCalendar;
  } else if (brief?.body) {
    const calSection = brief.body.split(/\nCALENDAR\n/)?.[1];
    if (calSection) {
      const calLines = calSection.split('\n');
      for (const cl of calLines) {
        const tm = cl.match(/^(\d{1,2}:\d{2})\s+(.+)/);
        if (tm) {
          const role = cl.match(/\[(.*?)\]/)?.[1] || null;
          const hasPREP = /PREP/.test(cl);
          calendarEntries.push({ time: tm[1], desc: tm[2], role, hasPREP });
        } else if (!cl.trim()) break;
      }
    }
  }

  // Priority engine
  const priorityStack = computePriorityStack({ tasks, goals, milestones, calendarEvents: calendarEntries, inbox });
  const topPriority = priorityStack[0] || null;

  // Venture balance
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const { scheduled } = autoScheduleTasks(calendarEntries, tasks.filter(t => !t.done), nowMin);
  const ventureBalance = computeVentureBalance(calendarEntries, scheduled);

  const recentAgentLog = (agentLog || []).filter(entry => {
    const age = Date.now() - new Date(entry.created_at).getTime();
    return age < 24 * 60 * 60 * 1000;
  });

  // Every mutation on this view goes through one refresh: refetch the data and
  // nudge the scheduler so the rest of the day re-flows around the change.
  const refresh = () => { onUpdate && onUpdate(); triggerScheduler && triggerScheduler(); };

  // The original version wrote to the DB but never refetched, so ticking a
  // task on the dashboard looked dead. completeTask also logs time entries,
  // matching the Tasks view behaviour.
  const toggleTask = async (t) => {
    await completeTask(t, !t.done);
    refresh();
  };

  // ─── Up Next queue: today's actionable tasks in execution order ───────────
  const queueTasks = (() => {
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    const todayKey = new Date().toISOString().split('T')[0];

    const open = tasks.filter(t =>
      !t.done && t.status !== 'done' && t.status !== 'blocked' &&
      // deferred past today stays out of today's queue
      !(t.earliest_start && new Date(t.earliest_start) > endOfToday)
    );

    // First block start per task today (server schedule = source of truth)
    const blockStart = new Map();
    (scheduledBlocks || []).filter(b => b.date === todayKey).forEach(b => {
      const cur = blockStart.get(b.task_id);
      if (cur === undefined || b.start_min < cur) blockStart.set(b.task_id, b.start_min);
    });

    const isTodayRelevant = (t) =>
      t.sort_order >= 1 || // manually ranked = pinned to today's queue
      blockStart.has(t.id) ||
      t.priority === 1 ||
      t.due_label === 'Today' || t.due_label === 'Overdue' ||
      (t.deadline && new Date(t.deadline) <= endOfToday);

    // Auto order: the priority engine's task ranking (same brain as NOW card)
    const autoRank = new Map();
    priorityStack.filter(i => i.type === 'task').forEach((i, idx) => autoRank.set(i.item.id, idx));

    return open
      .filter(isTodayRelevant)
      .sort((a, b) => {
        const am = a.sort_order >= 1 ? a.sort_order : null;
        const bm = b.sort_order >= 1 ? b.sort_order : null;
        if (am !== null || bm !== null) {
          if (am === null) return 1;
          if (bm === null) return -1;
          return am - bm;
        }
        const ab = blockStart.has(a.id) ? blockStart.get(a.id) : Infinity;
        const bb = blockStart.has(b.id) ? blockStart.get(b.id) : Infinity;
        if (ab !== bb) return ab - bb;
        return (autoRank.get(a.id) ?? Infinity) - (autoRank.get(b.id) ?? Infinity);
      })
      .slice(0, 10);
  })();

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}
      style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Slim header — greeting + day-at-a-glance, no passive hero space */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '2px 4px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, letterSpacing: '-0.03em', lineHeight: 1.1, margin: 0 }}>
          {greeting}, Mike.
        </h1>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {p1Tasks.length > 0 && (
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
              background: 'rgba(224,88,88,0.10)', color: T.red,
              border: '1px solid rgba(224,88,88,0.18)',
            }}>{p1Tasks.length} urgent</span>
          )}
          <span style={{
            fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
            background: 'rgba(74,158,204,0.10)', color: T.blue,
            border: '1px solid rgba(74,158,204,0.18)',
          }}>{queueTasks.length} queued today</span>
          <span style={{
            fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
            background: 'rgba(0,0,0,0.04)', color: T.textMuted,
            border: '1px solid rgba(0,0,0,0.05)',
          }}>{tasks.filter(t => !t.done).length} open</span>
        </div>
      </div>

      {/* Promises + Next Meeting pulse strip */}
      {(() => {
        const STALE_DAYS = 7;
        const open = (promises || []).filter(p => p.status === 'open');
        const overdue = open.filter(p => p.due_at && new Date(p.due_at) < new Date());
        const stale = open.filter(p => {
          if (p.due_at) return false;
          const ageDays = (Date.now() - new Date(p.committed_at).getTime()) / 86400000;
          return ageDays > STALE_DAYS;
        });
        const outgoingOpen = open.filter(p => p.direction === 'outgoing');

        // Find next external calendar meeting
        const now = new Date();
        const upcoming = (liveCalendar || []).filter(e => {
          if (!e.time) return false;
          const [h, m] = e.time.split(':').map(Number);
          const start = new Date(now);
          start.setHours(h, m, 0, 0);
          return start > now;
        }).sort((a, b) => (a.time || '').localeCompare(b.time || ''));
        const nextEvent = upcoming[0] || null;
        const nextEventBrief = nextEvent ? (meetingBriefs || []).find(b =>
          b.title === nextEvent.desc &&
          new Date(b.meeting_start).toDateString() === now.toDateString()
        ) : null;

        if (open.length === 0 && !nextEvent) return null;

        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Promises Pulse */}
            <Glass depth={1} hover style={{ padding: '16px 20px', cursor: 'pointer' }} onClick={() => onNavigate?.('promises')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: T.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Handshake size={14} color={T.amber} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Promises</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>
                    {open.length} open · {outgoingOpen.length} you owe
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {overdue.length > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 999,
                    background: T.redLight, color: T.red, border: '1px solid rgba(224,88,88,0.18)',
                  }}>{overdue.length} overdue</span>
                )}
                {stale.length > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 999,
                    background: T.amberLight, color: T.amber, border: `1px solid ${T.amberBorder}`,
                  }}>{stale.length} stale</span>
                )}
                {overdue.length === 0 && stale.length === 0 && open.length > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 500, padding: '4px 9px', borderRadius: 999,
                    background: T.greenLight, color: T.green, border: '1px solid rgba(90,158,120,0.18)',
                  }}>all on track</span>
                )}
                {open.length === 0 && (
                  <span style={{ fontSize: 11, color: T.textMuted, padding: '4px 0' }}>No open promises captured yet — visit Promises to scan.</span>
                )}
              </div>
            </Glass>

            {/* Next Meeting Brief */}
            <Glass depth={1} hover style={{ padding: '16px 20px', cursor: nextEvent ? 'pointer' : 'default' }} onClick={() => nextEvent && onNavigate?.('briefs')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: T.violetLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Sparkles size={14} color={T.violet} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Next meeting</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>
                    {nextEvent ? `${nextEvent.time} · ${nextEvent.desc?.slice(0, 36)}${nextEvent.desc && nextEvent.desc.length > 36 ? '…' : ''}` : 'Nothing on the calendar'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {nextEvent && nextEventBrief?.brief_markdown ? (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 999,
                    background: T.greenLight, color: T.green,
                  }}>brief ready</span>
                ) : nextEvent ? (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 9px', borderRadius: 999,
                    background: T.violetLight, color: T.violet,
                  }}>generate brief →</span>
                ) : null}
                {nextEvent?.role && ROLES[nextEvent.role] && (
                  <span style={{
                    fontSize: 11, fontWeight: 500, padding: '4px 9px', borderRadius: 999,
                    background: ROLES[nextEvent.role].light, color: ROLES[nextEvent.role].color,
                  }}>{ROLES[nextEvent.role].label}</span>
                )}
              </div>
            </Glass>
          </div>
        );
      })()}

      {/* Conflicts */}
      {conflicts.filter(c => !c.is_dismissed).map(c => {
        const isWarning = c.severity === 'warning';
        const typeLabel = {
          competing_p1: 'Priority Clash',
          deadline_collision: 'Deadline Collision',
          overcommitment: 'Overcommitted',
          goal_health_crisis: 'Goal at Risk',
          stale_milestone: 'Stale Milestone',
          role_imbalance: 'Role Imbalance',
        }[c.conflict_type] || '';
        return (
          <Glass key={c.id} style={{
            padding: '12px 18px',
            background: isWarning ? 'rgba(212,146,30,0.08)' : 'rgba(74,158,204,0.08)',
            border: `1px solid ${isWarning ? 'rgba(212,146,30,0.20)' : 'rgba(74,158,204,0.20)'}`,
          }} hover={false} specular={false}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <AlertTriangle size={15} color={isWarning ? T.amber : T.blue} style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
                  {typeLabel && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: isWarning ? 'rgba(212,146,30,0.15)' : 'rgba(74,158,204,0.15)', color: isWarning ? T.amber : T.blue, textTransform: 'uppercase' }}>{typeLabel}</span>}
                  <span style={{ fontSize: 13, fontWeight: 600, color: isWarning ? T.amber : T.blue }}>{c.title}</span>
                </div>
                {c.resolution && <div style={{ fontSize: 12, color: T.textMid, marginTop: 4, lineHeight: 1.4 }}>{c.resolution}</div>}
              </div>
              <button onClick={async () => { await supabase.from('conflicts').update({ is_dismissed: true }).eq('id', c.id); onUpdate(); }} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: T.textLight, flexShrink: 0,
              }} title="Dismiss"><X size={14} /></button>
            </div>
          </Glass>
        );
      })}

      {/* At-risk / overdue / blocked task warnings */}
      {(() => {
        const openTasks = tasks.filter(t => !t.done && t.status !== 'done');
        const overdueTasks = openTasks.filter(t => t.deadline && new Date(t.deadline) < new Date());
        const atRiskTasks = openTasks.filter(t => t.eta && t.deadline && new Date(t.eta) > new Date(t.deadline) && new Date(t.deadline) >= new Date());
        const blockedTasks = openTasks.filter(t => t.status === 'blocked');
        const warnings = [
          ...overdueTasks.map(t => ({ type: 'overdue', task: t })),
          ...atRiskTasks.map(t => ({ type: 'at-risk', task: t })),
          ...blockedTasks.map(t => ({ type: 'blocked', task: t })),
        ];
        if (warnings.length === 0) return null;
        return (
          <Glass style={{ padding: '14px 18px', background: 'rgba(224,88,88,0.05)', border: '1px solid rgba(224,88,88,0.12)' }} hover={false} specular={false}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <AlertTriangle size={14} color={T.red} />
              <span style={{ fontSize: 12, fontWeight: 600, color: T.red }}>
                {warnings.length} task{warnings.length !== 1 ? 's' : ''} need attention
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {warnings.slice(0, 5).map(w => (
                <div key={w.task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.6)' }}>
                  <button onClick={() => toggleTask(w.task)} title="Mark done" style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: '1.5px solid rgba(0,0,0,0.18)', background: 'transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }} />
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, flexShrink: 0,
                    color: w.type === 'overdue' ? T.red : w.type === 'blocked' ? T.violet : T.amber,
                    background: w.type === 'overdue' ? T.redLight : w.type === 'blocked' ? T.violetLight : T.amberLight,
                  }}>{w.type === 'overdue' ? 'OVERDUE' : w.type === 'blocked' ? 'BLOCKED' : 'AT RISK'}</span>
                  <span style={{ fontSize: 12, color: T.text, flex: 1 }}>{w.task.title}</span>
                  {w.task.deadline && (
                    <span style={{ fontSize: 10, color: T.textMuted }}>
                      {new Date(w.task.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  <DeferMenu task={w.task} onDeferred={refresh} />
                </div>
              ))}
              {warnings.length > 5 && (
                <div style={{ fontSize: 11, color: T.textMuted, paddingLeft: 8 }}>+{warnings.length - 5} more</div>
              )}
            </div>
          </Glass>
        );
      })()}

      {/* Morning Brief Hero (morning phase only) */}
      {config.showMorningBrief && brief && (
        <Glass style={{ padding: 28 }} hover={false} depth={2}>
          <SectionHeader icon={Sparkles} title="Morning Brief" subtitle={fmtRelative(brief.created_at)} />
          <Md style={{ fontSize: 14, lineHeight: 1.7 }}>{brief.body}</Md>
        </Glass>
      )}

      {/* Main 2-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20, alignItems: 'start' }}>

        {/* LEFT: Up Next queue + Smart Schedule */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <UpNextQueue tasks={queueTasks} onUpdate={onUpdate} triggerScheduler={triggerScheduler} />
          <SmartSchedule calendarEvents={calendarEntries} tasks={tasks} scheduledBlocks={scheduledBlocks} onToggleTask={toggleTask} onUpdate={refresh} onRefresh={refresh} />
        </div>

        {/* RIGHT: NOW + Pulse + Briefs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* NOW Card */}
          <NowCard priorityItem={topPriority} onUpdate={refresh} onNavigate={onNavigate} />

          {/* Goals Pulse — context, not action: collapsed by default */}
          <Collapsible
            icon={Target} iconBg={T.greenLight} iconColor={T.green}
            title="Goals Pulse"
            subtitle={`${goals.filter(g => g.status === 'active').length} active`}
            preview={goals.filter(g => g.status === 'active').sort((a, b) => (a.priority || 3) - (b.priority || 3)).slice(0, 3).map(g => g.title).join(' · ')}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {goals.filter(g => g.status === 'active').sort((a, b) => (a.priority || 3) - (b.priority || 3)).slice(0, 5).map(goal => {
                const r = ROLES[goal.roles?.slug];
                const gm = (milestones || []).filter(m => m.goal_id === goal.id);
                const doneCount = gm.filter(m => m.done).length;
                const progress = goal.type === 'quantitative'
                  ? pct(goal.metric_current, goal.metric_target)
                  : gm.length > 0 ? Math.round((doneCount / gm.length) * 100) : 0;
                const dl = fmtDeadline(goal.deadline);
                const health = goalHealth(goal, milestones || []);
                const h = health ? HEALTH[health] : null;
                return (
                  <div key={goal.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: T.text, flex: 1, marginRight: 8, lineHeight: 1.3 }}>{goal.title}</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        {h && <HealthBadge health={health} small />}
                        {dl && <span style={{ fontSize: 10, color: dl === 'Overdue' ? T.red : T.textMuted, fontWeight: dl === 'Overdue' ? 600 : 400 }}>{dl}</span>}
                      </div>
                    </div>
                    <ProgressBar value={progress} color={r?.color || T.amber} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                      <RoleBadge roleSlug={goal.roles?.slug} small />
                      <span style={{ fontSize: 10, color: T.textMuted }}>
                        {goal.type === 'quantitative' ? `${goal.metric_unit}${fmt(goal.metric_current)} / ${goal.metric_unit}${fmt(goal.metric_target)}` : `${doneCount}/${gm.length} milestones`}
                      </span>
                    </div>
                  </div>
                );
              })}
              {goals.filter(g => g.status === 'active').length === 0 && (
                <div style={{ textAlign: 'center', padding: '12px 0', color: T.textMuted, fontSize: 13 }}>No active goals.</div>
              )}
            </div>
          </Collapsible>

          {/* Collapsible briefs — show a 2-line preview on the card face */}
          {!config.showMorningBrief && (
            <Collapsible
              icon={Sparkles}
              title="Morning Brief"
              subtitle={brief ? fmtRelative(brief.created_at) : 'Not yet'}
              accent="linear-gradient(90deg, #f8d77a, #D4921E)"
              preview={brief?.body ? plainTextPreview(brief.body) : 'Runs automatically at 7am.'}
            >
              {brief ? <Md style={{ fontSize: 13 }}>{brief.body}</Md> : <div style={{ fontSize: 13, color: T.textMuted }}>Runs automatically at 7am.</div>}
            </Collapsible>
          )}

          {prioritisation && (
            <Collapsible
              icon={ListChecks}
              iconBg={T.violetLight}
              iconColor={T.violet}
              title="Prioritisation"
              subtitle={fmtRelative(prioritisation.created_at)}
              accent="linear-gradient(90deg, #B89BD8, #8B72C8)"
              preview={plainTextPreview(prioritisation.body)}
            >
              <Md style={{ fontSize: 13 }}>{prioritisation.body}</Md>
            </Collapsible>
          )}

          {recentAgentLog.length > 0 && (
            <Collapsible
              icon={Activity}
              iconBg='rgba(139,114,200,0.12)'
              iconColor={T.violet}
              title="Agent Activity"
              subtitle={`${recentAgentLog.length} actions`}
              preview={recentAgentLog[0] ? `${recentAgentLog[0].agents?.name ? recentAgentLog[0].agents.name + ': ' : ''}${recentAgentLog[0].action}` : null}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recentAgentLog.slice(0, 6).map((entry, j) => (
                  <div key={entry.id || j} style={{ display: 'flex', gap: 10, padding: '6px 8px', borderRadius: 6, background: j % 2 === 0 ? 'rgba(0,0,0,0.015)' : 'transparent', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0, minWidth: 40, fontFamily: "'SF Mono', Menlo, Monaco, monospace" }}>{fmtRelative(entry.created_at)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {entry.agents?.name && <span style={{ fontSize: 11, fontWeight: 600, color: T.violet, marginRight: 6 }}>{entry.agents.name}</span>}
                      <span style={{ fontSize: 12, color: T.textMid }}>{entry.action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Collapsible>
          )}

          {likenInsight && (
            <Collapsible
              icon={TrendingUp}
              title="Liken Insight"
              subtitle={fmtRelative(likenInsight.created_at)}
              accent="linear-gradient(90deg, #f8d77a, #D4921E)"
              preview={plainTextPreview(likenInsight.body)}
            >
              <Md style={{ fontSize: 13 }}>{likenInsight.body}</Md>
            </Collapsible>
          )}
        </div>
      </div>
    </motion.div>
  );
};
